"use client";

import { useEffect, useRef } from "react";
import { useAccessToken } from "@/hooks";
import {
  clearStaffReviewPresence,
  type StaffReviewPresenceResource,
  upsertStaffReviewPresence,
} from "./staff-review-presence";

const HEARTBEAT_MS = 30_000;

export function useStaffReviewPresence(
  resource: StaffReviewPresenceResource | null,
  isActive: boolean,
): { presenceSupported: boolean } {
  const accessToken = useAccessToken();
  const presenceSupportedRef = useRef(true);

  useEffect(() => {
    if (!isActive || !resource || !accessToken) return undefined;
    if (!presenceSupportedRef.current) return undefined;

    let cancelled = false;

    const heartbeat = async () => {
      const ok = await upsertStaffReviewPresence(accessToken, resource);
      if (!cancelled && !ok) {
        presenceSupportedRef.current = false;
      }
    };

    void heartbeat();
    const intervalId = window.setInterval(() => void heartbeat(), HEARTBEAT_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      void clearStaffReviewPresence(accessToken, resource);
    };
  }, [accessToken, isActive, resource]);

  return { presenceSupported: presenceSupportedRef.current };
}
